// useStateServiceability.js
// ----------------------------------------------------------------------
// Sprint-101b — State-level pickup serviceability hook.
//
// Calls POST /api/serviceability/v2/check with a 6-digit pickup pincode
// (or explicit state as fallback). Debounced 400ms with AbortController
// + request-seq counter to drop stale responses and avoid flicker.
//
// Shape of return:
//   { loading, serviceable, state, message, activeHubCount,
//     pincodeWhitelisted, error, refresh }
import { useEffect, useRef, useState, useCallback } from "react";
import api from "../services/api";

const DEBOUNCE_MS = 400;

export default function useStateServiceability({ pincode, state: explicitState }) {
  const [result, setResult] = useState({
    loading: false,
    serviceable: null,
    state: null,
    message: null,
    activeHubCount: null,
    pincodeWhitelisted: null,
    error: null,
  });

  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const seqRef = useRef(0);

  const run = useCallback(async () => {
    const mySeq = ++seqRef.current;
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    const pin = (pincode || "").toString().trim();
    const st = (explicitState || "").toString().trim();
    if (!pin && !st) {
      setResult({ loading: false, serviceable: null, state: null, message: null,
                  activeHubCount: null, pincodeWhitelisted: null, error: null });
      return;
    }
    if (pin && !/^\d{6}$/.test(pin)) {
      // 6-digit only — wait for the user to finish typing.
      setResult({ loading: false, serviceable: null, state: null, message: null,
                  activeHubCount: null, pincodeWhitelisted: null, error: null });
      return;
    }

    setResult((r) => ({ ...r, loading: true, error: null }));
    try {
      const body = {};
      if (pin) body.pincode = pin;
      if (st) body.state = st;
      const { data } = await api.post("/serviceability/v2/check", body, { signal: ac.signal });
      if (mySeq !== seqRef.current) return;
      setResult({
        loading: false,
        serviceable: !!data?.serviceable,
        state: data?.state || null,
        message: data?.message || null,
        activeHubCount: data?.active_hub_count ?? null,
        pincodeWhitelisted: data?.pincode_whitelisted ?? null,
        error: null,
      });
    } catch (e) {
      if (e?.name === "CanceledError" || e?.name === "AbortError") return;
      if (mySeq !== seqRef.current) return;
      setResult({
        loading: false,
        serviceable: null,
        state: null,
        message: null,
        activeHubCount: null,
        pincodeWhitelisted: null,
        error: "check-failed",
      });
    }
  }, [pincode, explicitState]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(run, DEBOUNCE_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [run]);

  return { ...result, refresh: run };
}
